import React, { FunctionComponent, useEffect, useState } from 'react';
import { RootState } from '../../app/store';
import { connect, ConnectedProps } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  black,
  fontColorPrim,
  fontColorSecond, green,
  red, white,
  yellow,
} from '../../assets/styledAssets/vars';
import { StyledSwitch } from '../../components/FormField/styles';
import { Card, StyledDelete, StyledSub, StyledSup } from './styles';
import { MoneyIcon } from '../../components/Icons/Icons.component';
import Moment from 'react-moment';
import Button from '../../components/Button/Button.component';
import { GridColumn } from '@progress/kendo-react-grid';
import SpaceService, {
  reviseSubscription,
  updateSubscription,
} from '../../service/Space.service';
import { Roles } from '../../domains/app';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import InviteMember from '../../components/InviteMember/InviteMember.component';
import { deleteSpace } from '../Spaces/Spaces.actions';
import { BillingUnit, SubcriptionPlan } from './settings';
import { PayPalButton } from 'react-paypal-button-v2';
import { fetchCurrentPlan } from './Settings.actions';
import moment from 'moment';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import { StyledLoader } from '../../assets/styledAssets/loader';

const connector = connect(
  (state: RootState) => ({
    selectedAccount: state.app.selectedAccount,
    currentPlan: state.settings.currentPlan,
    profile: state.app.user,
  }),
  {
    deleteSpace,
    fetchCurrentPlan,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Settings: FunctionComponent<PropsFromRedux> = ({
  selectedAccount,
  profile,
  deleteSpace,
  fetchCurrentPlan,
  currentPlan,
}) => {
  const intl = useIntl();
  const [payPlan, setPayPlan] = useState<BillingUnit>();
  const [listInvitations, setListInvitations] = useState<any[]>([]);
  const [billingHistory, setBillingHistory] = useState<any[]>([]);
  const [showInvite, setShowInvite] = useState<boolean>(false);
  const [listSubscriptions, setListSubscriptions] = useState<SubcriptionPlan[]>(
    [],
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationHandler, setConfirmationHandler] = useState<() => void>();
  const [billingInfo, setBillingInfo] = useState<any>();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const discount = 10;

  useEffect(() => {
    SpaceService.fetchListSubscriptions().then((res) => {
      setListSubscriptions(res);
    });
    fetchCurrentPlan();
    SpaceService.fetchListAdminInvitations(
      selectedAccount?.id as string,
      1000,
      0,
    ).then((res) => {
      setListInvitations((l) => {
        return l.concat(res.items);
      });
    });
    SpaceService.fetchListAdmins(selectedAccount?.id as string, 1000, 0).then(
      (res) => {
        setListInvitations((l) => {
          return l.concat(
            res.map((m) => {
              m.person.enrolled = m.enrolled;
              m.person.role = m.role;
              return m.person;
            }),
          );
        });
      },
    );

    SpaceService.getBillingHistory(selectedAccount?.id as string).then(
      (res) => {
        res = res.reverse();
        setBillingHistory(res);
      },
    );

    if (window.addEventListener) {
      window.addEventListener(
        'message',
        (event: any) => {
          try {
            const data = JSON.parse(event.data);
            console.log(data);
          } catch (e) {}
        },
        false,
      );
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentPlan) {
      setPayPlan(
        currentPlan.billingUnit === BillingUnit.YEAR
          ? BillingUnit.YEAR
          : BillingUnit.MONTH,
      );
    }
    setBillingInfo(undefined);
    if (currentPlan?.providerSubscriptionId) {
      SpaceService.getSubscriptionInfo(
        currentPlan?.providerSubscriptionId,
      ).then((res) => {
        setBillingInfo(res);
      });
    }
  }, [currentPlan]);

  const inviteAdmin = (email: string) => {
    SpaceService.inviteAdministrator(
      selectedAccount?.id as string,
      profile?.id as string,
      email,
    )
      .then((res) => {
        setListInvitations((list) => {
          list.push(res);
          return [...list];
        });
      })
      .finally(() => setShowInvite(false));
  };

  const handleDeleteSpace = () => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.confirmationDeleteSpace));
    setConfirmationHandler(() => {
      return () => {
        setShowLoader(true);
        SpaceService.cancelSubscription(selectedAccount?.id as string)
          .then(() => {
            deleteSpace(selectedAccount?.id as string);
            fetchCurrentPlan();
          })
          .finally(() => setShowLoader(false));
      };
    });
  };

  const updateSub = (
    subPlanId: string,
    subId?: string,
    orderId?: string,
    paymentId?: string,
    billingToken?: string,
  ) => {
    setShowLoader(true);
    updateSubscription(
      subPlanId,
      selectedAccount?.id as string,
      payPlan === BillingUnit.YEAR ? BillingUnit.YEAR : BillingUnit.MONTH,
      subId,
      orderId,
      paymentId,
      billingToken,
    )
      .then((res) => {
        fetchCurrentPlan();
      })
      .finally(() => setShowLoader(false));
  };

  const reviseSubscriptions = (planId: string) => {
    setShowLoader(true);

    reviseSubscription(currentPlan?.providerSubscriptionId as string, planId)
      .then((res) => {
        if (res.approveUrl) {
          window.open(res.approveUrl);
        }
      })
      .finally(() => setShowLoader(false));
  };

  const cancelSubscription = () => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.confirmationCancelSub));
    setConfirmationHandler(() => {
      return () => {
        setShowLoader(true);
        SpaceService.cancelSubscription(selectedAccount?.id as string)
          .then(() => {
            fetchCurrentPlan();
          })
          .finally(() => setShowLoader(false));
      };
    });
  };

  const getListRoles = (role: number) => {
    let listRoles = '';
    if (role & Roles.SpaceOwner) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.spaceOwner)}`
        : intl.formatMessage(messages.spaceOwner);
    }
    if (role & Roles.SpaceAdministrator) {
      listRoles += listRoles
        ? `, ${intl.formatMessage(messages.spaceAdministrator)}`
        : intl.formatMessage(messages.spaceAdministrator);
    }

    return listRoles;
  };

  return (
    <Wrap overflow={'auto'}>
      <Wrap width={'100%'} flexDir={'column'} alignItems={'center'}>
        <Wrap
          color={accentColorPrim}
          fs={'3.4rem'}
          margin={'0 0 3rem 0'}
          fw={'bold'}
        >
          {intl.formatMessage(messages.title)}
        </Wrap>
        <Wrap color={fontColorSecond} fs={'1.6rem'} margin={'0 0 3rem 0'}>
          {intl.formatMessage(messages.subtitle)}
        </Wrap>
        <Wrap alignItems={'center'} justifyCont={'center'}>
          <Wrap
            margin={'0 1.2rem 0 0'}
            fs={'1.4rem'}
            color={
              payPlan === BillingUnit.MONTH ? fontColorPrim : fontColorSecond
            }
          >
            {intl.formatMessage(messages.monthly)}
          </Wrap>
          <StyledSwitch
            checked={payPlan !== BillingUnit.YEAR}
            onChange={() =>
              setPayPlan(
                payPlan === BillingUnit.YEAR
                  ? BillingUnit.MONTH
                  : BillingUnit.YEAR,
              )
            }
          />
          <Wrap alignItems={'center'}>
            <Wrap
              margin={'0 1.2rem 0 1.2rem'}
              fs={'1.4rem'}
              color={
                payPlan === BillingUnit.YEAR ? fontColorPrim : fontColorSecond
              }
            >
              {intl.formatMessage(messages.yearly)}
            </Wrap>
            <Wrap
              color={black}
              padding={'.4rem .4rem'}
              fs={'1.2rem'}
              bgColor={yellow}
              br={'.4rem'}
            >
              {discount}% {intl.formatMessage(messages.disount)}
            </Wrap>
          </Wrap>
        </Wrap>
      </Wrap>
      <Wrap width={'100%'} justifyCont={'space-around'} alignItems={'unset'}>
        {listSubscriptions.map((sub, index) => (
          <Wrap width={'100%'} height={'auto'} maxWidth={'35rem'} margin={['1.6rem 0', '3rem 0']} key={`plan-${index}`}>
            <Card active={currentPlan?.subscriptionPlanId === sub.id}>
              <Wrap
                flexDir={'column'}
                alignItems={'center'}
                margin={'0 0 2rem 0'}
              >
                <Wrap fw={'bold'} fs={'2.4rem'}>
                  {sub.title}
                </Wrap>
                <Wrap margin={'3rem 0'}>
                  <StyledSup>{sub.currency === 'USD' ? '$' : '€'}</StyledSup>
                  <Wrap
                    color={accentColorPrim}
                    fs={'7rem'}
                    margin={'0 .5rem'}
                  >
                    {payPlan === BillingUnit.YEAR
                      ? sub.priceYearly
                      : sub.priceMonthly}
                  </Wrap>
                  <StyledSub>
                    /{payPlan === BillingUnit.YEAR ? 'y' : 'm'}
                  </StyledSub>
                </Wrap>
                <Wrap margin={'0 0 1rem 0'}>
                  <Wrap fw={'bold'}>
                    {sub.maxMemberCount > 100000000
                      ? intl.formatMessage(messages.unlimited)
                      : sub.maxMemberCount}
                  </Wrap>
                  &nbsp;{intl.formatMessage(messages.members)}
                </Wrap>
                <Wrap margin={'0 0 1rem 0'}>
                  <Wrap fw={'bold'}>
                    {sub.maxGroupCount > 100000000
                      ? intl.formatMessage(messages.unlimited)
                      : sub.maxGroupCount}
                  </Wrap>
                  &nbsp;
                  {intl.formatMessage(messages.groups)}
                </Wrap>
                {sub.questionnaireAbility && (
                  <Wrap margin={'0 0 1rem 0'}>
                    <Wrap fw={'bold'}>
                      {intl.formatMessage(messages.questionnaiers)}
                    </Wrap>
                  </Wrap>
                )}
              </Wrap>
              {!sub.productId && (
                <Button
                  primary
                  onClick={() =>
                    currentPlan?.subscriptionPlanId !== sub.id &&
                    cancelSubscription()
                  }
                >
                  {currentPlan?.subscriptionPlanId === sub.id
                    ? intl.formatMessage(messages.currPlan)
                    : intl.formatMessage(messages.changePlan)}
                </Button>
              )}
              {!!sub.productId &&
                !(
                  currentPlan?.subscriptionPlanId === sub.id &&
                  currentPlan?.billingUnit === payPlan
                ) && (
                  <div>
                    {!currentPlan?.subscriptionPlan.productId && (
                      <PayPalButton
                        options={{ vault: true }}
                        createSubscription={(data: any, actions: any) => {
                          return actions.subscription.create({
                            plan_id:
                              payPlan === BillingUnit.YEAR
                                ? sub.yearPlanId
                                : sub.monthPlanId,
                          });
                        }}
                        onApprove={(data: any, actions: any) => {
                          // Capture the funds from the transaction
                          return actions.subscription
                            .get()
                            .then(function (details: any) {
                              updateSub(
                                sub.id,
                                data.subscriptionID,
                                data.orderID,
                                data.paymentID,
                                data.billingToken,
                              );
                            });
                        }}
                      />
                    )}
                    {!!currentPlan?.subscriptionPlan.productId && (
                      <Button
                        primary
                        onClick={() => {
                          reviseSubscriptions(
                            payPlan === BillingUnit.YEAR
                              ? sub.yearPlanId
                              : sub.monthPlanId,
                          );
                        }}
                      >
                        {intl.formatMessage(messages.changePlan)}
                      </Button>
                    )}
                  </div>
                )}
              {!!sub.productId &&
                currentPlan?.subscriptionPlanId === sub.id &&
                currentPlan?.billingUnit === payPlan && (
                  <>
                    <Button primary>
                      {intl.formatMessage(messages.currPlan)}
                    </Button>
                    <Button warn onClick={() => cancelSubscription()}>
                      {intl.formatMessage(messages.cancelSubscr)}
                    </Button>
                  </>
                )}
            </Card>
          </Wrap>
        ))}
      </Wrap>
      <Wrap width={'100%'} justifyCont={['flex-start', 'space-between']} flexDir={['column', 'row']}>
        <Wrap width={'100%'} maxWidth={['100%', 'calc(50% - 1.6rem)']} justifyCont={'flex-start'} margin={['2rem 0 ', '0']} flexDir={'column'}>
          <Wrap
            color={fontColorPrim}
            fw={'bold'}
            margin={'1.6rem 0 1rem 0'}
            fs={'2.4rem'}
          >
            {intl.formatMessage(messages.billHistory)}
          </Wrap>
          <Wrap width={'100%'} flexDir={'column'}>
            {billingHistory.map((item, index) => (
              <Wrap
                key={`${index}-id`}
                alignItems={'center'}
                margin={'0 0 1rem 0'}
              >
                <MoneyIcon
                  key={`${index}-icon-id`}
                  color={index === 0 ? black : fontColorSecond}
                  width={'2rem'}
                />
                <Wrap
                  color={index === 0 ? black : fontColorSecond}
                  key={`${index}-wrap-id`}
                  margin={'.7rem 0 .7rem 1.4rem'}
                  alignItems={'center'}
                >
                  <Moment key={`${index}-moment-id`} format={'MMMM DD, YYYY'}>
                    {item.created}
                  </Moment>
                  &nbsp;-&nbsp;{item.amount} {item.currency}
                </Wrap>
              </Wrap>
            ))}
          </Wrap>
          {/*        <Wrap width={'100%'} justifyCont={'space-between'}>
          <Button secondary onClick={cancelSubscription}>
            {intl.formatMessage(messages.cancelSubscr)}
          </Button>
        </Wrap>*/}
        </Wrap>
        <Wrap width={'100%'} maxWidth={['100%', 'calc(50% - 1.6rem)']} flexDir={'column'}>
          <Wrap
            color={fontColorPrim}
            fw={'bold'}
            margin={'0 0 1rem 0'}
            fs={'2.4rem'}
          >
            {intl.formatMessage(messages.billInfo)}
          </Wrap>
          {billingInfo && (
            <Wrap justifyCont={'center'} flexDir={'column'} margin={'0 0 1rem 0'}>
              <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
                <Wrap
                  color={fontColorSecond}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  {intl.formatMessage(messages.uName)}:&nbsp;
                </Wrap>
                <Wrap
                  color={black}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  {billingInfo.name}
                </Wrap>
              </Wrap>
              <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
                <Wrap
                  color={fontColorSecond}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  {intl.formatMessage(messages.email)}:&nbsp;
                </Wrap>
                <Wrap
                  color={black}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  {billingInfo.email}
                </Wrap>
              </Wrap>
              <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
                <Wrap
                  color={fontColorSecond}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  {intl.formatMessage(messages.paidUntil)}:&nbsp;
                </Wrap>
                <Wrap
                  color={black}
                  margin={'.7rem 0 .7rem 0'}
                  alignItems={'center'}
                >
                  <Moment format={'MMMM DD, YYYY'}>
                    {billingInfo.paidUntil}
                  </Moment>
                </Wrap>
              </Wrap>
            </Wrap>
          )}
        </Wrap>
      </Wrap>
      <Wrap width={'100%'}>
        <Wrap
          width={'100%'}
          alignItems={['flex-start', 'center']}
          flexDir={['column', 'row']}
          margin={'3rem 0'}
          justifyCont={['flex-start', 'space-between']}
        >
          <Wrap color={accentColorPrim} fs={'2.4rem'} fw={'bold'} margin={['0 0 2rem 0', '0']}>
            {intl.formatMessage(messages.additionalSpaceAdmins)}
          </Wrap>
          <Button primary onClick={() => setShowInvite(true)}>
            {intl.formatMessage(messages.inviteAdmin)}
          </Button>
        </Wrap>
        <Wrap grow={'1'} overflow={'hidden'} width={'100%'} height={'100%'} hiddenXS>
          <StyledTable style={{ height: '100%' }} data={listInvitations}>
            <GridColumn field="firstName" title="FirstName" />
            <GridColumn field="lastName" title="LastName" />
            <GridColumn field="email" title="Email" />
            <GridColumn field="phone" title="Phone" width="150px" />
            <GridColumn
              field="role"
              title={intl.formatMessage(messages.role)}
              cell={(props) => <td>{getListRoles(props.dataItem.role)}</td>}
            />
            <GridColumn
              field="enrolled"
              title={intl.formatMessage(messages.enrolled)}
              width="150px"
              cell={(props) => (
                <td>
                  {!!props.dataItem.enrolled &&
                    moment(props.dataItem.enrolled).format('DD MMM YYYY')}
                </td>
              )}
            />
            <GridColumn
              field="invited"
              title={intl.formatMessage(messages.invited)}
              width="150px"
              cell={(props) => (
                <td>
                  {!!props.dataItem.invited &&
                    moment(props.dataItem.invited).format('DD MMM YYYY')}
                </td>
              )}
            />
            <GridColumn
              field="status"
              title={intl.formatMessage(messages.status)}
              width="150px"
              cell={(props) => (
                <td>
                  {props.dataItem.invitedToSpaceId ? (
                    <Wrap color={red}>
                      {intl.formatMessage(messages.pending)}
                    </Wrap>
                  ) : (
                    <Wrap color={accentColorPrim}>
                      {intl.formatMessage(messages.active)}
                    </Wrap>
                  )}
                </td>
              )}
            />
          </StyledTable>
          <Wrap flexDir={'column'} margin={'5rem 0 3rem'} width={'100%'}>
            <Wrap
              maxWidth={'35rem'}
              color={fontColorSecond}
              fs={'1.6rem'}
              margin={'0 0 3rem'}
            >
              {intl.formatMessage(messages.warn)}
            </Wrap>
            <Button warn className={StyledDelete} onClick={handleDeleteSpace}>
              {intl.formatMessage(messages.deleteSpace)}
            </Button>
          </Wrap>
        </Wrap>

        <Wrap grow={'1'} overflow={'auto'} width={'100%'} hiddenSM>
          {listInvitations.map((user: any, index) => (
            <Wrap
              flexDir={'column'}
              flexWrap={'wrap'}
              margin={'0 0 2rem 0'}
              width={'100%'}
              padding={'3rem'}
              br={'1.2rem'}
              bgColor={white}
              key={`card-${index}`}
            >
              <Wrap
                alignItems={'center'}
                justifyCont={'space-between'}
                width={'100%'}
                margin={'0 0 2rem 0'}
              >
                <Wrap fs={'2rem'} fw={'bold'}>
                  {user.firstName} {user.lastName}
                </Wrap>
                <Wrap>
                  {user.invitedToSpaceId ? (
                    <Wrap bgColor={accentColorPrim} br={'1rem'} padding={'.2rem .7rem'} color={white}>
                      {intl.formatMessage(messages.pending)}
                    </Wrap>
                  ) : (
                    <Wrap bgColor={green} br={'1rem'} padding={'.2rem .7rem'} color={white}>
                      {intl.formatMessage(messages.active)}
                    </Wrap>
                  )}
                </Wrap>
              </Wrap>
              <Wrap alignItems={'center'} margin={'0 0 2rem 0'}>
                <Wrap color={fontColorSecond} margin={'0 1.6rem 0 0'}>
                  {intl.formatMessage(messages.enrolled)}
                </Wrap>
                <Wrap color={fontColorSecond}>
                  {moment(user.enrolled).format('DD MMM YYYY')}
                </Wrap>
              </Wrap>
              <Wrap alignItems={'center'} margin={'0 0 1rem 0'}>
                <Wrap color={fontColorSecond}>{user.email}</Wrap>
              </Wrap>
              <Wrap alignItems={'center'}>
                <Wrap color={fontColorSecond}>{user.phone}</Wrap>
              </Wrap>
            </Wrap>
          ))}
          <Wrap flexDir={'column'} margin={'5rem 0 3rem'} width={'100%'}>
            <Wrap
              maxWidth={'35rem'}
              color={fontColorSecond}
              fs={'1.6rem'}
              margin={'0 0 3rem'}
            >
              {intl.formatMessage(messages.warn)}
            </Wrap>
            <Button warn className={StyledDelete} onClick={handleDeleteSpace}>
              {intl.formatMessage(messages.deleteSpace)}
            </Button>
          </Wrap>
        </Wrap>
        
      </Wrap>
      {showConfirmation && confirmationHandler && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={confirmationHandler}
        />
      )}
      {showInvite && (
        <DialogWrap
          title={intl.formatMessage(messages.inviteAdmin)}
          children={
            <InviteMember
              onCancel={() => setShowInvite(false)}
              onSave={inviteAdmin}
            />
          }
        />
      )}
      {showLoader && <StyledLoader />}
    </Wrap>
  );
};

export default connector(Settings);
